// Startup watchdog for the static splash in index.html (web only; robustness).
//
// The splash covers the JavaScript download and is removed by StartupGate on
// React's first frame. index.html reveals a Reload button by itself (CSS only)
// when that has not happened after 15 s, which covers scripts that never ran
// at all (a chunk that failed to download). This module covers startup code
// that did run but threw: `main.tsx` imports it first, so its listener is in
// place before the rest of the app's modules evaluate. It has no imports of its
// own, so nothing it depends on can fail first.

/** Id of the static splash in index.html. */
export const SPLASH_ID = 'boot-splash';

/** After an uncaught error, how long the app gets to draw its first frame before the splash says startup failed. */
export const ERROR_GRACE_MS = 1000;

/**
 * Switches the static splash to "Startup failed. Restart the app." (Flutter's
 * bootstrap error copy) with a Reload button. False when the app already
 * replaced the splash.
 */
export function showStartupFailure(doc: Document = document): boolean {
  const splash = doc.getElementById(SPLASH_ID);
  if (!splash) return false;
  if (splash.dataset.state) return true;
  splash.dataset.state = 'failed';
  splash.setAttribute('role', 'alert');
  splash.setAttribute('aria-label', 'Startup failed');
  return true;
}

/**
 * An error React could not contain unmounted the whole app (React removes the
 * tree after an uncaught render error). Before the first frame that is a
 * startup failure; afterwards the screen would stay blank, so the splash's
 * recovery screen comes back: "Something went wrong." with Reload.
 */
export function showCrashScreen(doc: Document = document): void {
  if (showStartupFailure(doc)) return;
  const screen = doc.createElement('div');
  screen.id = SPLASH_ID;
  screen.dataset.state = 'crashed';
  screen.setAttribute('role', 'alert');
  screen.setAttribute('aria-label', 'Something went wrong');
  const content = doc.createElement('div');
  content.className = 'boot-content';
  const recovery = doc.createElement('div');
  recovery.className = 'boot-recovery';
  const message = doc.createElement('p');
  message.className = 'boot-crashed-text';
  message.textContent = 'Something went wrong.';
  const reload = doc.createElement('a');
  reload.className = 'boot-reload';
  reload.href = '';
  reload.textContent = 'Reload';
  recovery.append(message, reload);
  content.append(recovery);
  screen.append(content);
  doc.body.append(screen);
}

/**
 * Watches for uncaught errors while the static splash is still up. Startup
 * code that throws (a module that fails while evaluating, `main.tsx` itself)
 * leaves the splash on screen forever; when the splash is still there a moment
 * after such an error, it switches to the failure state. Errors after the first
 * frame are left to the app. Resource load errors (`<link>`/`<script>`) do not
 * bubble to `window`, so a lazy chunk that fails to preload never counts.
 */
export function installBootWatchdog(target: Window = window): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onError = () => {
    const splash = target.document.getElementById(SPLASH_ID);
    if (!splash || splash.dataset.state) return;
    clearTimeout(timer);
    timer = setTimeout(() => showStartupFailure(target.document), ERROR_GRACE_MS);
  };
  target.addEventListener('error', onError);
  return () => {
    clearTimeout(timer);
    target.removeEventListener('error', onError);
  };
}

// Installed at import (main.tsx imports this module first). Only pages that
// carry the static splash: unit tests import the functions without it.
if (typeof document !== 'undefined' && document.getElementById(SPLASH_ID)) installBootWatchdog();
